import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface SolarPVBatteryCostData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  avgPVCost?: number;
  avgBatteryCost?: number;
}

// Helper function to get geofips for a location
async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

export default async function fetchSolarPVBatteryCostSupabase(location: string): Promise<SolarPVBatteryCostData> {
  try {
    // Get geofips for the location
    const geofips = await getGeofipsForLocation(location);
    
    if (!geofips) {
      // Return placeholder data if no geofips found
      return {
        chartData: {
          labels: ['2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'],
          datasets: [{
            label: 'Photovoltaic ($/kW)',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.1
          }, {
            label: 'Photovoltaic + Battery ($/kW)',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false,
            tension: 0.1
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Cost (Residential) - ${location} (No Geofips Found)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per kW ($)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No geofips found for ${location} in the population database. This location may not be available in the solar installation database.`,
        avgPVCost: 0,
        avgBatteryCost: 0
      };
    }

    // Query Supabase by geoid for server-side filtering
    let { data: techData, error } = await supabaseClient
      .from('tech_type_cost')
      .select('*')
      .eq('geoid', geofips)
      .order('year', { ascending: true });

    if (error) {
      console.error('Error fetching Solar PV Battery Cost data:', error);
      throw error;
    }

    // Fallback: if no rows by geoid, try location_name (case-insensitive)
    if (!techData || techData.length === 0) {
      const { data: byLocation, error: locErr } = await supabaseClient
        .from('tech_type_cost')
        .select('*')
        .ilike('location_name', `%${location}%`)
        .order('year', { ascending: true });
      if (locErr) {
        console.error('Error fetching Solar PV Battery Cost by location_name:', locErr);
      } else if (byLocation && byLocation.length > 0) {
        techData = byLocation;
      }
    }

    if (!techData || techData.length === 0) {
      // Return placeholder data if no records found
      return {
        chartData: {
          labels: ['2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'],
          datasets: [{
            label: 'Photovoltaic ($/kW)',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.1
          }, {
            label: 'Photovoltaic + Battery ($/kW)',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false,
            tension: 0.1
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Cost (Residential) - ${location} (No Data Available)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per kW ($)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No solar PV battery cost data available for ${location}. This may indicate that the location has not yet been added to the solar installation database, or that solar installations are not yet recorded for this area.`,
        avgPVCost: 0,
        avgBatteryCost: 0
      };
    }

    // Process the data to create chart datasets
    if (!techData || techData.length === 0) {
      // Return placeholder data if no records found
      return {
        chartData: {
          labels: ['2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'],
          datasets: [{
            label: 'Photovoltaic ($/kW)',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.1
          }, {
            label: 'Photovoltaic + Battery ($/kW)',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false,
            tension: 0.1
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Cost (Residential) - ${location} (No Data Available)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per kW ($)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No solar PV battery cost data available for ${location}. This may indicate that the location has not yet been added to the solar installation database, or that solar installations are not yet recorded for this area.`,
        avgPVCost: 0,
        avgBatteryCost: 0
      };
    }

    // At this point, techData is guaranteed to be non-null and non-empty
    const validTechData = techData!;
    
    const allYears = Array.from(new Set(validTechData.map(item => Number(item.year)))).sort((a: number, b: number) => a - b);
    const years = allYears.filter(year => year >= 2015 && year <= 2025); // Filter to 2015-2025
    
    // Extract PV and Battery costs
    const pvData = years.map(year => {
      const yearData = validTechData.find(item => Number(item.year) === year);
      if (!yearData || !yearData.photovoltaic) return null;
      return Number(yearData.photovoltaic);
    });

    const batteryData = years.map(year => {
      const yearData = validTechData.find(item => Number(item.year) === year);
      if (!yearData || !yearData.photovoltaic_battery_energy_storage) return null;
      return Number(yearData.photovoltaic_battery_energy_storage);
    });

    const chartData = {
      labels: years.map(String),
      datasets: [
        {
          label: 'Photovoltaic ($/kW)',
          data: pvData,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: false,
          tension: 0.1,
          spanGaps: false
        },
        {
          label: 'Photovoltaic + Battery ($/kW)',
          data: batteryData,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: false,
          tension: 0.1,
          spanGaps: false
        }
      ]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Solar PV Cost Comparison (Residential) - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          filter: function(tooltipItem: any) {
            return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined;
          },
          callbacks: {
            label: function(context: any) {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              if (value === null || value === undefined) {
                return undefined;
              }
              return `${label}: $${value.toFixed(2)}/kW`;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year'
          },
          ticks: {
            display: true,
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0
          }
        },
        y: {
          title: {
            display: true,
            text: 'Cost per kW ($)'
          },
          beginAtZero: true
        }
      }
    };

    // Calculate average costs
    const pvCosts = pvData.filter(v => v !== null && v !== undefined && v > 0) as number[];
    const batteryCosts = batteryData.filter(v => v !== null && v !== undefined && v > 0) as number[];
    
    const avgPVCost = pvCosts.length > 0 ? pvCosts.reduce((sum, v) => sum + v, 0) / pvCosts.length : 0;
    const avgBatteryCost = batteryCosts.length > 0 ? batteryCosts.reduce((sum, v) => sum + v, 0) / batteryCosts.length : 0;

    const narrative = `${location} shows average costs of $${avgPVCost.toFixed(2)} per kW for standalone photovoltaic systems and $${avgBatteryCost.toFixed(2)} per kW for photovoltaic systems with battery storage. The cost difference reflects the additional expense of battery energy storage systems, which provide energy independence and backup power capabilities.`;

    return {
      chartData,
      chartOptions,
      narrative,
      avgPVCost,
      avgBatteryCost
    };

  } catch (error) {
    console.error('Error in fetchSolarPVBatteryCostSupabase:', error);
    throw error;
  }
}

